import {
  LayoutGrid, Search, Boxes, Package, Heart, BookOpen, Bell, ClipboardList, FileText, Inbox,
  ArrowLeftRight, MessageSquare, PlusCircle, Upload, LayoutDashboard, BarChart3, Layers, Database,
  Building2, Settings, DollarSign, CreditCard, Bug, HelpCircle, Compass, ShoppingCart, Handshake,
  Wrench, Shield, Tag,
} from 'lucide-react';

const isManager = (u) => ['admin', 'group_admin', 'site_manager'].includes(u?.role);
const isAdmin = (u) => u?.role === 'admin';

export const NAV_SECTIONS = [
  {
    key: 'marketplace', labelKey: 'nav.sec.marketplace', icon: ShoppingCart, color: 'text-orange-500',
    items: [
      { key: 'hub', to: '/hub', icon: LayoutGrid, labelKey: 'nav.hub', iconColor: 'text-orange-500' },
      { key: 'my-materials', to: '/my-materials', icon: Boxes, labelKey: 'nav.myMaterials' },
      { key: 'my-listings', to: '/my-listings', icon: Tag, labelKey: 'nav.myListings' },
      { key: 'watchlist', to: '/watchlist', icon: Heart, labelKey: 'nav.watchlist' },
      { key: 'wanted', to: '/wanted', icon: BookOpen, labelKey: 'nav.wanted', iconColor: 'text-purple-500' },
      { key: 'my-offers', to: '/my-offers', icon: BookOpen, labelKey: 'nav.myOffers', iconColor: 'text-orange-500' },
      { key: 'saved-searches', to: '/saved-searches', icon: Bell, labelKey: 'nav.savedSearches', iconColor: 'text-orange-500' },
      { key: 'standard-materials', to: '/matching?tab=standard', icon: ClipboardList, labelKey: 'nav.standardMaterials' },
    ],
  },
  {
    key: 'trade', labelKey: 'nav.sec.trade', icon: Handshake, color: 'text-blue-500',
    items: [
      { key: 'requests', to: '/requests?tab=incoming', icon: FileText, labelKey: 'nav.requests', badge: 'requests', testId: 'nav-requests-link' },
      { key: 'group-requests', to: '/group-requests', icon: Inbox, labelKey: 'nav.groupRequests' },
      { key: 'transactions', to: '/transactions', icon: ArrowLeftRight, labelKey: 'nav.transactions' },
      { key: 'matching', to: '/matching', icon: ArrowLeftRight, labelKey: 'nav.matching', iconColor: 'text-orange-500' },
      { key: 'messages', to: '/messages', icon: MessageSquare, labelKey: 'nav.messages', badge: 'messages', iconColor: 'text-blue-500' },
    ],
  },
  {
    key: 'management', labelKey: 'nav.sec.management', icon: Wrench, color: 'text-emerald-600', show: isManager,
    items: [
      { key: 'create-material', to: '/material/new', icon: PlusCircle, labelKey: 'create.newTitle' },
      { key: 'import', to: '/import', icon: Upload, labelKey: 'nav.import' },
      { key: 'dashboard', to: '/dashboard', icon: LayoutDashboard, labelKey: 'nav.dashboard' },
      { key: 'analytics', to: '/analytics', icon: BarChart3, labelKey: 'nav.analytics', iconColor: 'text-blue-500' },
      { key: 'scenario', to: '/scenario', icon: BarChart3, labelKey: 'nav.scenario' },
      { key: 'my-group', to: '/my-group', icon: Layers, labelKey: 'nav.myGroup' },
      { key: 'master-data', to: '/master-data', icon: Database, labelKey: 'nav.masterData' },
    ],
  },
  {
    key: 'portals', labelKey: 'nav.sec.portals', icon: Layers, color: 'text-violet-500',
    show: (u) => u?.is_seller || u?.role === 'group_admin' || isAdmin(u) || u?.role === 'external',
    items: [
      { key: 'my-company', to: '/my-company', icon: Building2, labelKey: 'nav.myCompany', show: (u) => u?.role === 'external' },
      { key: 'seller-portal', to: '/seller-portal', icon: BarChart3, labelKey: 'nav.sellerPortal', iconColor: 'text-green-500', show: (u) => !!u?.is_seller },
      { key: 'group-admin', to: '/group-admin', icon: Layers, labelKey: 'nav.groupAdminPortal', iconColor: 'text-violet-500', show: (u) => u?.role === 'group_admin' || isAdmin(u) },
    ],
  },
  {
    key: 'platform-admin', labelKey: 'nav.sec.platformAdmin', icon: Shield, color: 'text-red-500', show: isAdmin,
    items: [
      { key: 'admin', to: '/admin', icon: Settings, labelKey: 'nav.admin' },
      { key: 'commission', to: '/commission', icon: DollarSign, labelKey: 'nav.commission', iconColor: 'text-orange-500' },
      { key: 'subscriptions', to: '/subscriptions', icon: CreditCard, labelKey: 'nav.subscriptions', iconColor: 'text-violet-500' },
    ],
  },
  {
    key: 'settings', labelKey: 'nav.sec.settings', icon: Settings, color: 'text-muted-foreground',
    items: [
      { key: 'my-reports', to: '/my-reports', icon: Bug, labelKey: 'nav.myReports', iconColor: 'text-orange-500' },
      { key: 'guide', to: '/guide', icon: HelpCircle, labelKey: 'nav.guide', iconColor: 'text-teal-500' },
      { key: 'restart-tour', action: 'restartTour', icon: Compass, labelKey: 'onboarding.restartTour', iconColor: 'text-orange-500' },
      { key: 'notification-settings', to: '/notification-settings', icon: Bell, labelKey: 'nav.notificationSettings', iconColor: 'text-orange-400' },
    ],
  },
];

export const PRIMARY_NAV = [
  { key: 'hub', to: '/hub', icon: LayoutGrid, labelKey: 'nav.hub' },
  { key: 'marketplace', to: '/search', icon: Search, labelKey: 'nav.marketplace' },
  { key: 'wanted', to: '/wanted', icon: BookOpen, labelKey: 'nav.wanted' },
  { key: 'requests', to: '/requests?tab=incoming', icon: FileText, labelKey: 'nav.requests', badge: 'requests' },
  { key: 'messages', to: '/messages', icon: MessageSquare, labelKey: 'nav.messages', badge: 'messages' },
  { key: 'transactions', to: '/transactions', icon: ArrowLeftRight, labelKey: 'nav.transactions' },
];

export const QUICK_ACTIONS = [
  { key: 'create-material', to: '/material/new', icon: PlusCircle, labelKey: 'create.newTitle' },
  { key: 'create-listing', to: '/listing/new', icon: Tag, labelKey: 'nav.createListing' },
  { key: 'create-wanted', to: '/wanted?tab=my', icon: BookOpen, labelKey: 'nav.wanted' },
  { key: 'import', to: '/import', icon: Upload, labelKey: 'nav.import', show: isManager },
];

export const visibleSections = (user) =>
  NAV_SECTIONS.filter((s) => !s.show || s.show(user))
    .map((s) => ({ ...s, items: s.items.filter((i) => !i.show || i.show(user)) }))
    .filter((s) => s.items.length > 0);

export const visibleActions = (user) => QUICK_ACTIONS.filter((a) => !a.show || a.show(user));

export const isActivePath = (pathname, to) => {
  const base = to.split('?')[0];
  if (base === '/search') return pathname === '/search' || pathname.startsWith('/material/') || pathname.startsWith('/listing');
  return pathname === base || pathname.startsWith(`${base}/`);
};

// first URL segment → i18n label key (used by breadcrumbs)
export const ROUTE_LABELS = {
  hub: 'nav.hub', search: 'nav.marketplace', listings: 'nav.marketplace', material: 'nav.marketplace',
  listing: 'nav.marketplace', 'my-materials': 'nav.myMaterials', 'my-listings': 'nav.myListings',
  watchlist: 'nav.watchlist', wanted: 'nav.wanted', 'my-offers': 'nav.myOffers', 'saved-searches': 'nav.savedSearches',
  requests: 'nav.requests', 'group-requests': 'nav.groupRequests', transactions: 'nav.transactions',
  matching: 'nav.matching', messages: 'nav.messages', dashboard: 'nav.dashboard', analytics: 'nav.analytics',
  scenario: 'nav.scenario', 'my-group': 'nav.myGroup', 'master-data': 'nav.masterData', import: 'nav.import',
  admin: 'nav.admin', commission: 'nav.commission', subscriptions: 'nav.subscriptions',
  'seller-portal': 'nav.sellerPortal', 'group-admin': 'nav.groupAdminPortal', guide: 'nav.guide',
  'my-reports': 'nav.myReports', 'notification-settings': 'nav.notificationSettings', 'my-company': 'nav.myCompany',
  'standard-materials': 'nav.standardMaterials',
};
